<template>
    <div v-if="filesWithUrls.length" class="request-file-list">
        <div v-if="imageFiles.length" class="request-file-photo-grid">
            <div
                v-for="file in imageFiles"
                :key="file.id"
                class="request-file-photo-card"
            >
                <b-button
                    v-if="canEdit"
                    size="sm"
                    variant="light"
                    class="request-file-remove"
                    title="Удалить"
                    @click.stop="onRemoveClick(file)"
                >
                    <i class="fas fa-times"></i>
                </b-button>
                <button
                    type="button"
                    class="request-file-photo"
                    :title="file.originalName"
                    @click="openFileViewer(file)"
                >
                    <img
                        :src="file.thumbnailUrl"
                        :alt="file.originalName"
                        loading="lazy"
                        decoding="async"
                    />
                </button>
                <a
                    :href="file.url"
                    target="_blank"
                    rel="noopener"
                    class="request-file-name"
                    :title="file.originalName"
                    @click="handleFileLinkClick($event, file)"
                >
                    {{ file.originalName }}
                </a>
            </div>
        </div>

        <div v-if="documentFiles.length" class="request-file-attachment-list">
            <div
                v-for="file in documentFiles"
                :key="file.id"
                class="request-file-attachment"
            >
                <a
                    :href="file.url"
                    target="_blank"
                    rel="noopener"
                    class="request-file-attachment-link"
                    :title="file.originalName"
                    @click="handleFileLinkClick($event, file)"
                >
                    <span class="request-file-attachment-icon">
                        <i :class="fileIconClass(file)"></i>
                    </span>
                    <span class="request-file-attachment-text">
                        <span class="request-file-attachment-name">{{ file.originalName }}</span>
                        <small v-if="file.file_size" class="text-muted">{{ formatFileSize(file.file_size) }}</small>
                    </span>
                </a>
                <b-button
                    v-if="canEdit"
                    size="sm"
                    variant="outline-danger"
                    class="request-file-attachment-remove"
                    title="Удалить"
                    @click="onRemoveClick(file)"
                >
                    <i class="fas fa-times"></i>
                </b-button>
            </div>
        </div>

    </div>
</template>

<script>
import {
    fileIconClass as getFileIconClass,
    formatFileSize as formatViewerFileSize,
    isImageFile as isViewerImageFile,
    withQueryParams
} from '@/utils/fileViewer';

export default {
    name: "FileList",
    props: {
        list: {
            type: Array,
            default: () => []
        },
        canEdit: {
            type: Boolean,
            default: false
        },
        requestId: {
            type: [Number, String],
            required: false
        }
    },
    computed: {
        filesWithUrls() {
            const { list, requestId } = this;
            if (!list || !list.length) return [];

            return list.map(file => {
                const url = this.withAuthToken(file.url || this.fileUrl(file, requestId));
                const isImage = this.isImageFile(file);

                return {
                    ...file,
                    url,
                    thumbnailUrl: isImage ? withQueryParams(url, { thumb: '1', w: 128, h: 96 }) : null,
                    originalName: file.originalName || file.file_name || 'Файл',
                    isImage
                };
            });
        },
        imageFiles() {
            return this.filesWithUrls.filter(file => file.isImage);
        },
        documentFiles() {
            return this.filesWithUrls.filter(file => !file.isImage);
        },
        viewerFiles() {
            return this.filesWithUrls.map(file => ({
                id: file.id,
                name: file.originalName,
                url: file.url,
                downloadUrl: file.url,
                thumbnailUrl: file.thumbnailUrl,
                mimeType: file.mimeType || file.mime_type,
                size: file.file_size || file.size,
                isImage: file.isImage,
                raw: file
            }));
        }
    },
    methods: {
        fileUrl(file, requestId) {
            if (!file || !file.id || !requestId) {
                return '#';
            }
            return `/api/requests/items/${requestId}/files/${file.id}`;
        },
        withAuthToken(url) {
            const token = this.$store && this.$store.state ? this.$store.state.token : null;
            if (!url || url === '#' || !token || String(url).indexOf('gjuid=') !== -1) {
                return url;
            }
            return withQueryParams(url, { gjuid: token });
        },
        isImageFile(file) {
            return isViewerImageFile(file);
        },
        fileIconClass(file) {
            return `${getFileIconClass(file)} text-secondary`;
        },
        formatFileSize(bytes) {
            return formatViewerFileSize(bytes);
        },
        openFileViewer(file) {
            if (!file || !file.id) {
                return;
            }
            if (!this.$fileViewer) {
                window.open(file.url, `_blank`);
                return;
            }
            this.$fileViewer.open(this.viewerFiles, {
                startId: file.id,
                source: `request-files`
            });
        },
        handleFileLinkClick(event, file) {
            if (event && event.preventDefault) {
                event.preventDefault();
            }
            this.openFileViewer(file);
        },
        onRemoveClick(file) {
            this.$bvModal.msgBoxConfirm('Вы действительно хотите удалить файл?', { contentClass: 'argus-glass-dialog',
                cancelTitle: 'Отмена',
                okTitle: 'Удалить',
                okVariant: 'danger'
            })
            .then(confirm => {
                if (confirm) {
                    this.$emit('remove', file.id);
                }
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.request-file-list {
    width: 100%;
}

.request-file-photo-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.request-file-photo-card {
    position: relative;
    width: 112px;
}

.request-file-remove {
    position: absolute;
    right: 4px;
    top: 4px;
    z-index: 2;
    width: 24px;
    height: 24px;
    padding: 0;
    border-radius: 50%;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.request-file-photo {
    display: block;
    width: 112px;
    height: 84px;
    padding: 0;
    border: 1px solid #d6dce3;
    border-radius: 4px;
    background: #f8f9fa;
    overflow: hidden;
    cursor: pointer;
}

.request-file-photo:hover {
    border-color: #007bff;
}

.request-file-photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.request-file-name {
    display: block;
    margin-top: 4px;
    font-size: 0.78rem;
    line-height: 1.2;
    color: #495057;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.request-file-attachment-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 8px;
    max-width: 520px;
}

.request-file-attachment {
    display: flex;
    align-items: center;
    gap: 6px;
}

.request-file-attachment-link {
    display: flex;
    align-items: center;
    min-width: 0;
    flex: 1;
    padding: 6px 8px;
    border: 1px solid #d6dce3;
    border-radius: 4px;
    color: #343a40;
    background: #fff;
}

.request-file-attachment-link:hover {
    text-decoration: none;
    border-color: #007bff;
    background: #f8fbff;
}

.request-file-attachment-icon {
    width: 28px;
    min-width: 28px;
    text-align: center;
    font-size: 1.2rem;
}

.request-file-attachment-text {
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.request-file-attachment-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.2;
}

.request-file-attachment-remove {
    width: 30px;
    min-width: 30px;
    height: 30px;
    padding: 0;
}
</style>
